import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {CommonHttpProviderService} from '../provider-services/common-http.provider-service';
import {LOCAL_STORAGE_KEY_CONSTANTS} from '../../core/constants/local-key-storage.constants';
import {Router} from '@angular/router';
import {APPLICATION_ROUTES_CONSTANTS} from '../../core/constants/application-routes.constants';
import {HTTP_API_ENDPOINTS_CONSTANTS} from '../../core/constants/http/http-endpoints-constants';
import {MapperService} from './mapper.service';
import {TApiResponseModel} from '../../typings/models/common/t-api-response-model';
import {SignUpRequestModel, SignUpResponseModel} from '../../typings/models/authentication/sign-up.model';
import {SignUpRequestDto, SignUpResponseDto} from '../../typings/dtos/authentication/sign-up.dto';
import {RefreshTokenRequestDto, RefreshTokenResponseDto} from '../../typings/dtos/refresh-token.dto';
import {TCommonModel} from '../../typings/models/common/t-common-model';
import {HttpClient, HttpRequest} from '@angular/common/http';
import {environment} from '../../../environments/environment';
import {HTTP_PARAMS_CONSTANTS} from '../../core/constants/http/http-params.constants';
import {UserLocalDataModel} from '../../typings/models/user-local-data.model';
import {CommonService} from './common.service';
import {LocalStorageProviderService} from '../provider-services/local-storage.provider-service';
import {map, take} from 'rxjs/operators';
import {GoogleSsoRequestDto, GoogleSsoResponseDto} from '../../typings/dtos/google-sso.dto';
import {
  GetVerificationCodeRequestModel,
  GetVerificationCodeResponseModel
} from '../../typings/models/authentication/get-verification-code.model';
import {
  GetVerificationCodeRequestDto,
  GetVerificationCodeResponseDto
} from '../../typings/dtos/authentication/get-verification-code.dto';
import {
  SubmitVerificationCodeRequestModel,
  SubmitVerificationCodeResponseModel
} from '../../typings/models/authentication/submit-verification-code.model';
import {
  SubmitVerificationCodeRequestDto,
  SubmitVerificationCodeResponseDto
} from '../../typings/dtos/authentication/submit-verification-code.dto';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  constructor(
    private router: Router,
    private http: HttpClient,
    private readonly mapper: MapperService,
    private commonHttpProviderService: CommonHttpProviderService,
    private localStorageProviderService: LocalStorageProviderService,
    private commonService: CommonService
  ) {
    this.initializeApp();
  }

  private loginStatusSubject!: BehaviorSubject<boolean>;
  loginStatus$!: Observable<boolean>;

  public accessToken!: string | null;
  public refreshToken!: string | null;

  private getLoginStatus(): boolean {
    // an expired access token doesn't mean the user is logged out; as long as a refresh token exists,
    // the response interceptor will get a new access token on the first 401.
    const refreshToken = this.localStorageProviderService.getItem(LOCAL_STORAGE_KEY_CONSTANTS.REFRESH_TOKEN);
    return !!refreshToken;
  }

  private initializeApp(): void {
    this.loginStatusSubject = new BehaviorSubject<boolean>(this.getLoginStatus());
    this.loginStatus$ = this.loginStatusSubject.asObservable();

    this.setTokensFromStorage();
    this.commonService.setUserInformationFromApi();
  }

  addInterceptRequestHeaders(request: HttpRequest<any>) {
    return request.clone({
      setHeaders: {
        'Authorization': `Bearer ${this.accessToken}`
      }
    });
  }

  getVerificationCode(requestModel: GetVerificationCodeRequestModel): Observable<TApiResponseModel<GetVerificationCodeResponseModel | null>> {
    return this.commonHttpProviderService.post<
      GetVerificationCodeRequestDto,
      GetVerificationCodeResponseDto,
      GetVerificationCodeRequestModel,
      GetVerificationCodeResponseModel
    >(
      GetVerificationCodeRequestDto,
      GetVerificationCodeResponseDto,
      GetVerificationCodeRequestModel,
      GetVerificationCodeResponseModel,
      HTTP_API_ENDPOINTS_CONSTANTS.AUTHENTICATION.GET_VERIFICATION_CODE_ASYNC,
      null,
      requestModel,
      null,
      true,
      true
    );
  }

  submitVerificationCode(requestModel: SubmitVerificationCodeRequestModel): Observable<TApiResponseModel<SubmitVerificationCodeResponseModel | null>> {
    return this.commonHttpProviderService.post<
      SubmitVerificationCodeRequestDto,
      SubmitVerificationCodeResponseDto,
      SubmitVerificationCodeRequestModel,
      SubmitVerificationCodeResponseModel
    >(
      SubmitVerificationCodeRequestDto,
      SubmitVerificationCodeResponseDto,
      SubmitVerificationCodeRequestModel,
      SubmitVerificationCodeResponseModel,
      HTTP_API_ENDPOINTS_CONSTANTS.AUTHENTICATION.SUBMIT_VERIFICATION_CODE_ASYNC,
      null,
      requestModel,
      null,
      true,
      true
    );
  }

  signUp(requestModel: SignUpRequestModel): Observable<TApiResponseModel<SignUpResponseModel | null>> {
    return this.commonHttpProviderService.post<
      SignUpRequestDto,
      SignUpResponseDto,
      SignUpRequestModel,
      SignUpResponseModel
    >(
      SignUpRequestDto,
      SignUpResponseDto,
      SignUpRequestModel,
      SignUpResponseModel,
      HTTP_API_ENDPOINTS_CONSTANTS.AUTHENTICATION.SIGN_UP_ASYNC,
      null,
      requestModel
    );
  }

  // NOTE: uses HttpClient directly (not CommonHttpProviderService), so the HTTP status is preserved on failure;
  // the interceptor needs it to tell "refresh token rejected" (logout) apart from network/server errors (don't logout).
  refreshTokens(refreshToken: string): Observable<RefreshTokenResponseDto> {
    const requestDto: RefreshTokenRequestDto = {accessToken: this.accessToken ?? '', refreshToken};

    return this.http.post<TApiResponseModel<RefreshTokenResponseDto>>(
      `${environment.apiBaseUrl}/${HTTP_API_ENDPOINTS_CONSTANTS.AUTHENTICATION.REFRESH_TOKEN_ASYNC}`,
      requestDto
    ).pipe(
      map(res => {
        if (!res.success || !res.data) {
          throw new Error(res.message || 'Refreshing token failed');
        }
        return res.data;
      })
    );
  }

  // tokens may have been rotated by another browser tab
  syncTokensFromStorage(): void {
    this.setTokensFromStorage();
  }

  googleSso(requestDto: GoogleSsoRequestDto): Observable<TApiResponseModel<GoogleSsoResponseDto | null>> {
    return this.commonHttpProviderService.post<
      GoogleSsoRequestDto,
      GoogleSsoResponseDto,
      TCommonModel,
      TCommonModel
    >(
      GoogleSsoRequestDto,
      GoogleSsoResponseDto,
      null,
      null,
      HTTP_API_ENDPOINTS_CONSTANTS.AUTHENTICATION.GOOGLE_SSO_ASYNC,
      null,
      null,
      requestDto,
      false,
      false
    ) as Observable<TApiResponseModel<GoogleSsoResponseDto | null>>;
  }

  internalLogin(model: UserLocalDataModel) {
    this.commonService.setUserInformation(model);
    this.setTokens(model.accessToken, model.refreshToken)
    this.loginStatusSubject.next(true);
  }

  internalLogout(): void {
    this.commonService.removeUserInformation();
    this.removeTokens();
    this.loginStatusSubject.next(false);

    this.commonService.navigateTo(APPLICATION_ROUTES_CONSTANTS.ROUTES.HOME);
  }

  setTokens(accessToken: string, refreshToken: string): void {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    this.setTokensInStorage(accessToken, refreshToken);
  }

  private setTokensFromStorage(): void {
    this.accessToken = this.localStorageProviderService.getItem(LOCAL_STORAGE_KEY_CONSTANTS.ACCESS_TOKEN);
    this.refreshToken = this.localStorageProviderService.getItem(LOCAL_STORAGE_KEY_CONSTANTS.REFRESH_TOKEN);
  }

  private setTokensInStorage(accessToken: string, refreshToken: string): void {
    this.localStorageProviderService.setItem(LOCAL_STORAGE_KEY_CONSTANTS.ACCESS_TOKEN, accessToken);
    this.localStorageProviderService.setItem(LOCAL_STORAGE_KEY_CONSTANTS.REFRESH_TOKEN, refreshToken);
  }

  private removeTokens(): void {
    this.accessToken = null;
    this.refreshToken = null;
    this.removeTokensFromStorage();
  }

  private removeTokensFromStorage(): void {
    this.localStorageProviderService.removeItem(LOCAL_STORAGE_KEY_CONSTANTS.ACCESS_TOKEN);
    this.localStorageProviderService.removeItem(LOCAL_STORAGE_KEY_CONSTANTS.REFRESH_TOKEN);
  }

  private decodeAccessToken(token: string): any {
    try {
      const payload = token.split('.')[1];
      return JSON.parse(atob(payload));
    } catch {
      return null;
    }
  }

  private isTokenExpired(token: string | null): boolean {
    if (token === null) return true;

    const decoded = this.decodeAccessToken(token);
    if (!decoded || !decoded.exp) return true;

    const now = Math.floor(Date.now() / 1000);
    return decoded.exp < now;
  }
}
